import { CustomTooltip } from '@AMIEWEB/Common/Tooltips';
import { ISkillSetGroup } from '@AMIEWEB/Facility/FacilityStore/Facility.model';
import { transformSkillSetGroupData } from '@AMIEWEB/Order/OrderCreationForm/Components/UnitDetail/UnitDetailDataTransformer';
import { selectOrderDetailsUIView, selectWeeklyGrossPayDetails } from '@AMIEWEB/Order/Store/Order.selectors';
import { orderCreationReducer, orderCreationSliceKey } from '@AMIEWEB/Order/Store/OrderCreation.redux';
import { orderCreationSaga } from '@AMIEWEB/Order/Store/OrderCreation.saga';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import { Box, Grid, IconButton, Skeleton } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import { orderCreationNewActions } from '../../NewOrder/Store/OrderCreation.actions';
import { WrapperComponent } from '../CreateOrder/Common/WrapperComponent';
import { WrapperComponentWithSubSections } from '../CreateOrder/Common/WrapperComponentWithSubSections';
import { WrapperForExperience } from '../CreateOrder/Common/WrapperForExperience';
import AdditionalRequirementsOne from '../CreateOrder/PreviewAndSubmit/AdditionalRequirementsOne';
import AdditionalRequirementsTwo from '../CreateOrder/PreviewAndSubmit/AdditionalRequirementsTwo';
import Experiences from '../CreateOrder/PreviewAndSubmit/Experiences';
import OrderDetailsOne from '../CreateOrder/PreviewAndSubmit/OrderDetailsOne';
import OrderDetailsTwo from '../CreateOrder/PreviewAndSubmit/OrderDetailsTwo';
import OrderRequirements from '../CreateOrder/PreviewAndSubmit/OrderRequirements';
import { selectOrderLicenseOptions, selectSkillsetDataUIFormat } from '../Store/Order.selectors';
import Qualifications, { useQualificationStyles } from '../CreateOrder/PreviewAndSubmit/Qualification/Qualifications';
import { CustomCircularProgress } from '@AMIEWEB/Order/OrderDetails/OrderDetailsDesign';
import { usePromiseTracker } from 'react-promise-tracker';
import { orderCreationNewReducer, orderCreationNewSliceKey } from '../Store/OrderCreation.redux';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { orderCreationNewSaga } from '../Store/OrderCreation.saga';
import { OrderCreationStepEnum } from '../types';
import {
  FacilityUnitActions,
  FacilityUnitKey,
  FacilityUnitReducer,
} from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/UnitTab/Store/FacilityUnit.redux';
import { facilityUnitsSaga } from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/UnitTab/Store/FacilityUnit.saga';
import LaunchIcon from '@mui/icons-material/Launch';
import { useReadOnly, userRoles } from 'oidc/userRoles';
import { Authorized } from 'oidc/userHelper';
import { selectUser } from 'oidc/user.selectors';
import { WeeklyGrossPay } from './WeeklyGrossPay/WeeklyGrossPay';
import { makeStyles } from 'tss-react/mui';
import { ff_order_global_margintool, ff_order_global_margintool_ui} from 'app/constants/FeatureFlags/MarginTool/Keys';
import { useDecision } from '@optimizely/react-sdk';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { selectIsMarginOrderBaseDetailsValid } from '@AMIEWEB/Order/Store/Order.selectors';
const useStyles = makeStyles()(theme => ({
  customContentClass: {
    border: 'none',
    padding: '0px',
    margin: '0 12px 12px 12px',
    backgroundColor: 'inherit',
    borderRadius: '0px',
  },
}));

export const OrderSummary = isNewOrderCreation => {
  const { classes } = useStyles();
  useInjectReducer({ key: orderCreationNewSliceKey, reducer: orderCreationNewReducer });
  useInjectSaga({ key: orderCreationNewSliceKey, saga: orderCreationNewSaga });
  const { promiseInProgress: isUpdating } = usePromiseTracker({ area: 'update-order', delay: 0 });
  const { promiseInProgress: getWeeklyGrossPayTracker } = usePromiseTracker({ area: 'get-weekly-gross-pay', delay: 0 });
  useInjectReducer({ key: orderCreationSliceKey, reducer: orderCreationReducer });
  useInjectSaga({ key: orderCreationSliceKey, saga: orderCreationSaga });
  useInjectReducer({ key: FacilityUnitKey, reducer: FacilityUnitReducer });
  useInjectSaga({ key: FacilityUnitKey, saga: facilityUnitsSaga });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useNavigate();
  const [featureFlagOrderMarginTool] = useDecision(ff_order_global_margintool);
  const [featureFlagOrderMarginToolUI] = useDecision(ff_order_global_margintool_ui);
  const orderDetailsUIView = useSelector(selectOrderDetailsUIView);
  const weeklyGrossPayDetails = useSelector(selectWeeklyGrossPayDetails);
  const skillSetGroupsOrderStore = useSelector(selectOrderDetailsUIView);
  const skillSetGroupsStore = useSelector(selectSkillsetDataUIFormat);
  const orderLicenseOptions = useSelector(selectOrderLicenseOptions);
  const user = useSelector(selectUser);

  const isMarginOrderBaseDetailsValid = useSelector(selectIsMarginOrderBaseDetailsValid);

  const [isEditQualification, setIsEditQualification] = React.useState(false);
  const [isOpenFromDetailsPage, setIsOpenFromDetailsPage] = React.useState(false);
  const [refreshEnabled, setrRefreshEnabled] = React.useState(true);
  const { readOnly } = useReadOnly();

  let skillSetGroups: ISkillSetGroup;

  if (isNewOrderCreation) {
    skillSetGroups = skillSetGroupsOrderStore?.orderDetails?.skillSetGroup;
  } else {
    skillSetGroups = transformSkillSetGroupData(skillSetGroupsStore);
  }

  const handleEditOrder = (e, value) => {
    if (orderDetailsUIView?.orderDetails?.location?.state && orderLicenseOptions?.length === 0)
      dispatch(orderCreationNewActions.getOrderLicenseOptions(orderDetailsUIView?.orderDetails?.location?.state ?? ''));

    const selectOrderUiDetails = orderDetailsUIView?.orderDetails?.orderId;
    if (selectOrderUiDetails) {
      if (value !== OrderCreationStepEnum.OrderRequirements) {
        dispatch(orderCreationNewActions.setActiveStep(value));
      }
      dispatch(orderCreationNewActions.onRequestEditOrder({ orderId: selectOrderUiDetails }));
      history(`/order/${selectOrderUiDetails}/edit`);
    }
  };

  React.useEffect(() => {
    dispatch(FacilityUnitActions.getIssuedBy());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetailsUIView]);

  const showEditIcon = Authorized(
    [userRoles.accountManagement_Leadership, userRoles.accountManagement_TeamMember],
    user.userInfo,
  );

  const handleEQ = () => {
    setIsEditQualification(true);
    setIsOpenFromDetailsPage(true);
  };
  const handleVQ = () => {
    setIsEditQualification(false);
    setIsOpenFromDetailsPage(true);
  };

  const handleCloseModal = () => {
    setIsOpenFromDetailsPage(false);
  };

  const handleRefreshWeeklyPay = () => {
    dispatch(orderDataActions.getWeeklyGrossPay(Number(orderDetailsUIView?.orderDetails?.orderId)));
  };

  return (
    <Box>
      {isUpdating && <CustomCircularProgress />}
      <Grid container xs={12} spacing={2} margin={'0px'}>
        <Grid xs={4} paddingRight={'12px'}>
          {(featureFlagOrderMarginTool.enabled && featureFlagOrderMarginToolUI.enabled && (orderDetailsUIView?.orderDetails?.isOrderMarginBaseDetailsIsValid === true || isMarginOrderBaseDetailsValid === true)) && (
            <Grid direction={'column'}>
              <WrapperComponent
                title={t('order.orderDetails.weeklyGrossPay.title')}
                children={
                  getWeeklyGrossPayTracker ? (
                    <Skeleton variant="rectangular" width="100%" height={200} />
                  ) : (
                    <WeeklyGrossPay groups={weeklyGrossPayDetails} setRereshEnaled={setrRefreshEnabled} />
                  )
                }
                customContentClass={classes.customContentClass}
                icon={<RefreshIcon handleRefresh={handleRefreshWeeklyPay} refreshEnabled={refreshEnabled} />}
              />
            </Grid>
          )}
          <Grid direction={'column'}>
            <WrapperComponent
              title={t('order.createUnit.qualifications.title')}
              children={
                <Qualifications
                  isNewOrderCreation={false}
                  isEditQualification={isEditQualification}
                  isOpenFromDetailsPage={isOpenFromDetailsPage}
                  handleCloseModal={handleCloseModal}
                />
              }
              icon={showEditIcon ? <EditIcon handleEditOrder={handleEQ} /> : <PopUpIcon handleViewOrder={handleVQ} />}
            />
          </Grid>
          <WrapperComponent
            title={t('order.newOrderCreation.tabs.orderRequirements')}
            children={<OrderRequirements isNewOrderCreation={false} />}
            icon={!readOnly && <EditIcon handleEditOrder={e => handleEditOrder(e, OrderCreationStepEnum.OrderRequirements)} />}
          />
        </Grid>
        <Grid xs={8}>
          <WrapperForExperience
            title={t('facility.units.experience.title', {
              expCount: skillSetGroups?.groups?.[0]?.skillsets?.length ?? 0,
            })}
            children={<Experiences isNewOrderCreation={false} />}
            icon={!readOnly && <EditIcon handleEditOrder={e => handleEditOrder(e, OrderCreationStepEnum.OrderRequirements)} />}
          />
          <WrapperComponentWithSubSections
            title={t('order.newOrderCreation.tabs.orderDetails')}
            childrenOne={<OrderDetailsOne isNewOrderCreation={false} />}
            childrenTwo={<OrderDetailsTwo isNewOrderCreation={false} />}
            icon={!readOnly && <EditIcon handleEditOrder={e => handleEditOrder(e, OrderCreationStepEnum.OrderDetails)} />}
          />
          <WrapperComponentWithSubSections
            title={t('placement.profile.tabs.additionalRequirements')}
            childrenOne={<AdditionalRequirementsOne isNewOrderCreation={false} />}
            childrenTwo={<AdditionalRequirementsTwo isNewOrderCreation={false} />}
            icon={!readOnly && <EditIcon handleEditOrder={e => handleEditOrder(e, OrderCreationStepEnum.AdditionalRequirements)} />}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export const EditIcon = ({ handleEditOrder }) => {
  const { t } = useTranslation();
  return (
    <CustomTooltip tooltipContent={t('facility.tooltip.edit')}>
      <IconButton color="secondary" size="medium" onClick={handleEditOrder}>
        <CreateOutlinedIcon fontSize="small" />
      </IconButton>
    </CustomTooltip>
  );
};

export const PopUpIcon = ({ handleViewOrder }) => {
  const { t } = useTranslation();
  const { classes } = useQualificationStyles();
  return (
    <CustomTooltip tooltipContent={t('order.viewFullDescription')}>
      <LaunchIcon className={classes.facilityGeneralLaunchIcon} onClick={handleViewOrder} />
    </CustomTooltip>
  );
};

export const RefreshIcon = ({ handleRefresh, refreshEnabled }) => {
  const { t } = useTranslation();
  return (
    <CustomTooltip tooltipContent={t('order.orderDetails.weeklyGrossPay.refresh')}>
      <IconButton color="secondary" size="medium" onClick={handleRefresh} disabled={!refreshEnabled}>
        <RefreshRoundedIcon fontSize="small" />
      </IconButton>
    </CustomTooltip>
  );
};
